import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import StatusBadge from '@/components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Send, Check, X, Edit, Trash2, FileDown, FileSpreadsheet, MessageCircle, CheckCircle2, MessageSquarePlus, Link2 } from 'lucide-react';

const ROLE_COLORS = {
  Faculty: 'bg-blue-100 text-blue-700',
  CIC: 'bg-cyan-100 text-cyan-700',
  AcademicHead: 'bg-indigo-100 text-indigo-700',
  PrincipalSSV: 'bg-emerald-100 text-emerald-700',
  SuperAdmin: 'bg-amber-100 text-amber-700',
  CEO: 'bg-rose-100 text-rose-700',
};
const ROLE_LABELS = { Faculty: 'Faculty', CIC: 'CIC', AcademicHead: 'Head', PrincipalSSV: 'Principal', SuperAdmin: 'Super Admin', CEO: 'CEO' };
const COMMENT_ROLES = ['CIC', 'AcademicHead', 'PrincipalSSV', 'CEO'];
const SECTION_LABELS = { regular_duties: 'Academics / Regular Duties', responsibilities: 'Responsibilities', course_marketings: 'Initiatives' };

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [visibleToFaculty, setVisibleToFaculty] = useState(true);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [closingItem, setClosingItem] = useState(null); // {section, itemId}
  const [closingDate, setClosingDate] = useState(new Date().toISOString().split('T')[0]);
  const [commentingItem, setCommentingItem] = useState(null); // {section, itemId}
  const [quickMsg, setQuickMsg] = useState('');
  const [quickVisibleToFaculty, setQuickVisibleToFaculty] = useState(true);
  const [sendingQuick, setSendingQuick] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const messagesEndRef = useRef(null);

  // Latest message per role, indexed by `${section}::${itemId}`
  const latestByRoleByItem = (() => {
    const map = {};
    for (const m of messages) {
      if (!m.activity_section || !m.activity_item_id) continue;
      const key = `${m.activity_section}::${m.activity_item_id}`;
      if (!map[key]) map[key] = {};
      const cur = map[key][m.sender_role];
      if (!cur || new Date(m.created_at) > new Date(cur.created_at)) {
        map[key][m.sender_role] = m;
      }
    }
    return map;
  })();

  const flashHighlight = (id) => {
    setHighlightId(id);
    setTimeout(() => setHighlightId(prev => (prev === id ? null : prev)), 2200);
  };

  const scrollToMessage = (msgId) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      flashHighlight(`msg-${msgId}`);
    }
  };

  const scrollToRow = (section, itemId) => {
    const el = document.getElementById(`row-${section}-${itemId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      flashHighlight(`row-${section}-${itemId}`);
    }
  };

  const handleSendQuickComment = async () => {
    if (!commentingItem || !quickMsg.trim()) return;
    setSendingQuick(true);
    try {
      const isHigherHierarchy = ['AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role);
      const flag = isHigherHierarchy ? quickVisibleToFaculty : undefined;
      const { data: msg } = await api.sendMessage(id, quickMsg.trim(), flag, commentingItem.section, commentingItem.itemId);
      setMessages(prev => [...prev, msg]);
      setQuickMsg('');
      setCommentingItem(null);
    } catch (err) {
      alert('Failed to send comment');
    } finally {
      setSendingQuick(false);
    }
  };

  // Cell renderer: shows latest message from `role` for (section, itemId), clickable to jump
  const CommentCell = ({ role, section, itemId }) => {
    const m = latestByRoleByItem[`${section}::${itemId}`]?.[role];
    if (!m) return <span className="text-slate-300 text-xs">—</span>;
    return (
      <button
        type="button"
        data-testid={`comment-cell-${section}-${itemId}-${role}`}
        onClick={() => scrollToMessage(m._id)}
        className="text-left text-xs text-slate-700 hover:text-blue-700 hover:underline line-clamp-2 max-w-[160px] block"
        title={`${m.message}\n— ${m.sender_name} · ${new Date(m.created_at).toLocaleString()}`}
      >
        {m.message}
      </button>
    );
  };

  // Inline "+ Comment" trigger for a row (any logged-in user can tag)
  const CommentTrigger = ({ section, itemId }) => (
    <button
      type="button"
      data-testid={`add-comment-${section}-${itemId}`}
      onClick={() => { setCommentingItem({ section, itemId }); setQuickMsg(''); setQuickVisibleToFaculty(true); }}
      className="text-blue-600 hover:text-blue-700 text-xs font-semibold flex items-center gap-1 whitespace-nowrap"
      title="Add a tagged comment for this entry"
    >
      <MessageSquarePlus className="w-3.5 h-3.5" /> Comment
    </button>
  );

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (type) => {
    setExportLoading(type);
    try {
      const res = type === 'pdf' ? await api.exportReportPDF(id) : await api.exportReportExcel(id);
      const ext = type === 'pdf' ? 'pdf' : 'xlsx';
      const fname = `HIMT_Report_${report?.faculty_name || 'report'}_${report?.report_date || report?.week_from || 'export'}.${ext}`;
      triggerDownload(new Blob([res.data]), fname);
    } catch (err) {
      alert('Export failed. Please try again.');
    } finally {
      setExportLoading(null);
    }
  };

  useEffect(() => {
    async function load() {
      try {
        const [reportRes, msgsRes] = await Promise.all([
          api.getReport(id),
          api.getMessages(id),
        ]);
        setReport(reportRes.data);
        setMessages(msgsRes.data);
        // Mark messages as read
        api.markMessagesRead(id).catch(() => {});
      } catch (err) {
        console.error('Failed to load report:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async () => {
    setActionLoading(true);
    try {
      await api.submitReport(id);
      const { data } = await api.getReport(id);
      setReport(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReview = async (action) => {
    setActionLoading(true);
    try {
      await api.reviewReport(id, { action, comments });
      const { data } = await api.getReport(id);
      setReport(data);
      setComments('');
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api.deleteReport(id);
      navigate('/reports');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSendMessage = async () => {
    if (!newMsg.trim()) return;
    setSendingMsg(true);
    try {
      const isHigherHierarchy = ['AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role);
      // Only pass the flag when sender is higher hierarchy (default: hidden); for Faculty/CIC backend defaults to visible
      const flag = isHigherHierarchy ? visibleToFaculty : undefined;
      const { data: msg } = await api.sendMessage(id, newMsg.trim(), flag, null, null);
      setMessages(prev => [...prev, msg]);
      setNewMsg('');
    } catch (err) {
      alert('Failed to send message');
    } finally {
      setSendingMsg(false);
    }
  };

  const handleCloseItem = async () => {
    if (!closingItem) return;
    try {
      await api.closeItem(id, closingItem.section, closingItem.itemId, closingDate);
      const { data } = await api.getReport(id);
      setReport(data);
      setClosingItem(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to close item');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-400 text-sm">Loading report...</div>;
  }

  if (!report) {
    return <div className="text-center py-16 text-slate-400">Report not found</div>;
  }

  const hasOwnReportAccess = ['Faculty', 'CIC', 'AcademicHead', 'PrincipalSSV'].includes(user?.role) || user?.also_faculty;
  const canSubmit = hasOwnReportAccess && report.faculty_id === user?._id && ['Draft', 'Rejected'].includes(report.report_status);
  const canEdit = hasOwnReportAccess && report.faculty_id === user?._id && ['Draft', 'Rejected'].includes(report.report_status);
  const canDelete = hasOwnReportAccess && report.faculty_id === user?._id && report.report_status === 'Draft';
  const isNotOwnReport = report.faculty_id !== user?._id;
  const canCICReview = user?.role === 'CIC' && report.report_status === 'Submitted' && isNotOwnReport;
  const canAHReview = user?.role === 'AcademicHead' && report.report_status === 'CICForwarded' && isNotOwnReport;
  const canPrincipalReview = user?.role === 'PrincipalSSV' && report.report_status === 'AHForwarded' && isNotOwnReport;

  const statusColors = { InProgress: 'bg-amber-50', Completed: 'bg-emerald-50', Closed: 'bg-emerald-50', Overdue: 'bg-red-50' };

  return (
    <div data-testid="report-detail-page" className="space-y-4 sm:space-y-6 animate-fade-in max-w-[1400px]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            data-testid="back-to-reports"
            onClick={() => navigate('/reports')}
            className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors shrink-0"
          >
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Report Detail</p>
            <h1 className="text-lg sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {report.week_from} — {report.week_to}
            </h1>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2">
              <StatusBadge status={report.report_status} />
              <span className="text-sm text-slate-500">by {report.faculty_name}</span>
              {report.submitted_date && (
                <span className="text-xs text-slate-400">Submitted {new Date(report.submitted_date).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap ml-12 sm:ml-0">
          <Button
            data-testid="export-pdf-btn"
            onClick={() => handleExport('pdf')}
            disabled={exportLoading === 'pdf'}
            className="bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 text-sm"
          >
            <FileDown className="w-3.5 h-3.5 mr-1.5" />
            {exportLoading === 'pdf' ? 'Exporting...' : 'PDF'}
          </Button>
          <Button
            data-testid="export-excel-btn"
            onClick={() => handleExport('excel')}
            disabled={exportLoading === 'excel'}
            className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 text-sm"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
            {exportLoading === 'excel' ? 'Exporting...' : 'Excel'}
          </Button>
          {canEdit && (
            <Button
              data-testid="edit-report-btn"
              onClick={() => navigate(`/reports/${id}/edit`)}
              className="bg-slate-200 text-slate-800 hover:bg-slate-300 text-sm"
            >
              <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
          )}
          {canDelete && (
            <Button
              data-testid="delete-report-btn"
              onClick={handleDelete}
              className="bg-red-100 text-red-700 hover:bg-red-200 text-sm"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
          )}
          {canSubmit && (
            <Button
              data-testid="submit-report-btn"
              onClick={handleSubmit}
              disabled={actionLoading}
              className="bg-blue-600 text-white hover:bg-blue-700 text-sm"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" /> Submit
            </Button>
          )}
        </div>
      </div>

      {/* Section 1: Regular Duties */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold">1</div>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Academics / Regular Duties</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {report.regular_duties?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-200 text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    {['Date', 'Topics', 'Presea Cls', 'Presea Prac', 'Online Cls', 'Post Sea', 'Total Hrs', 'Days Absent', 'Remarks', 'CIC', 'Head', 'Principal', 'CEO', 'Status', ''].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-200 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.regular_duties.map((d, i) => {
                    const rowId = `row-regular_duties-${d.id}`;
                    const isHi = highlightId === rowId;
                    return (
                    <tr key={i} id={rowId} className={`border-b border-slate-200 ${statusColors[d.status] || ''} ${isHi ? 'ring-2 ring-blue-400 ring-inset' : ''} transition-shadow`}>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700">{d.date || '—'}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700">{d.topics || '—'}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700 text-center">{d.presea_classes_hrs}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700 text-center">{d.presea_practicals_hrs}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700 text-center">{d.online_classes_hrs}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700 text-center">{d.post_sea_classes_hrs}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-900 text-center font-bold">{d.total_hours}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700 text-center">{d.days_absent}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-600">{d.remarks || '—'}</td>
                      <td className="px-3 py-2 border border-slate-200 align-top"><CommentCell role="CIC" section="regular_duties" itemId={d.id} /></td>
                      <td className="px-3 py-2 border border-slate-200 align-top"><CommentCell role="AcademicHead" section="regular_duties" itemId={d.id} /></td>
                      <td className="px-3 py-2 border border-slate-200 align-top"><CommentCell role="PrincipalSSV" section="regular_duties" itemId={d.id} /></td>
                      <td className="px-3 py-2 border border-slate-200 align-top"><CommentCell role="CEO" section="regular_duties" itemId={d.id} /></td>
                      <td className="px-3 py-2 border border-slate-200"><StatusBadge status={d.status} /></td>
                      <td className="px-3 py-2 border border-slate-200">
                        <div className="flex items-center gap-2">
                          <CommentTrigger section="regular_duties" itemId={d.id} />
                          {d.status === 'Overdue' && (
                            <button data-testid={`close-duty-${i}`} onClick={() => setClosingItem({ section: 'regular_duties', itemId: d.id })} className="text-emerald-600 hover:text-emerald-700 text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Close
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm py-4 text-center">No entries</p>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Responsibilities (Grouped) */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center text-white text-xs font-bold">2</div>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Responsibilities</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {report.responsibilities?.length > 0 ? (() => {
            const groups = [];
            const seen = new Set();
            report.responsibilities.forEach(r => {
              const gn = r.group_name || 'General';
              if (!seen.has(gn)) { seen.add(gn); groups.push(gn); }
            });
            return (
              <div className="space-y-4">
                {groups.map(gn => {
                  const rows = report.responsibilities.filter(r => (r.group_name || 'General') === gn);
                  return (
                    <div key={gn} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-slate-400" />
                        <span className="text-sm font-bold text-slate-700">{gn}</span>
                        <span className="text-xs text-slate-400">({rows.length} entries)</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="bg-slate-50/50">
                              {['Date', 'Topics', 'Description', 'Time Spent', 'Planned', 'Deadline', 'Closing Date', 'CIC', 'Head', 'Principal', 'CEO', 'Status', ''].map(h => (
                                <th key={h} className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, i) => {
                              const rowId = `row-responsibilities-${r.id}`;
                              const isHi = highlightId === rowId;
                              return (
                              <tr key={i} id={rowId} className={`border-b border-slate-100 ${statusColors[r.status] || ''} ${isHi ? 'ring-2 ring-blue-400 ring-inset' : ''} transition-shadow`}>
                                <td className="px-3 py-2 text-slate-700">{r.date || '—'}</td>
                                <td className="px-3 py-2 text-slate-700">{r.topics || '—'}</td>
                                <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">{r.description || '—'}</td>
                                <td className="px-3 py-2 text-slate-700 text-center">{r.time_spent_hrs}</td>
                                <td className="px-3 py-2 text-slate-700 text-center">{r.planned_hrs}</td>
                                <td className="px-3 py-2 text-slate-700">{r.deadline || '—'}</td>
                                <td className="px-3 py-2 text-slate-700">{r.closing_out_date || '—'}</td>
                                <td className="px-3 py-2 align-top"><CommentCell role="CIC" section="responsibilities" itemId={r.id} /></td>
                                <td className="px-3 py-2 align-top"><CommentCell role="AcademicHead" section="responsibilities" itemId={r.id} /></td>
                                <td className="px-3 py-2 align-top"><CommentCell role="PrincipalSSV" section="responsibilities" itemId={r.id} /></td>
                                <td className="px-3 py-2 align-top"><CommentCell role="CEO" section="responsibilities" itemId={r.id} /></td>
                                <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <CommentTrigger section="responsibilities" itemId={r.id} />
                                    {r.status === 'Overdue' && (
                                      <button data-testid={`close-resp-${r.id}`} onClick={() => setClosingItem({ section: 'responsibilities', itemId: r.id })} className="text-emerald-600 hover:text-emerald-700 text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Close
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })() : (
            <p className="text-slate-400 text-sm py-4 text-center">No entries</p>
          )}
        </CardContent>
      </Card>

      {/* Section 3: Initiatives */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-emerald-600 flex items-center justify-center text-white text-xs font-bold">3</div>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Initiatives</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {report.course_marketings?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-200 text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    {['Date', 'Topics', 'Description', 'Deadline', 'CIC', 'Head', 'Principal', 'CEO', 'Status', ''].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-200 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.course_marketings.map((m, i) => {
                    const rowId = `row-course_marketings-${m.id}`;
                    const isHi = highlightId === rowId;
                    return (
                    <tr key={i} id={rowId} className={`border-b border-slate-200 ${statusColors[m.status] || ''} ${isHi ? 'ring-2 ring-blue-400 ring-inset' : ''} transition-shadow`}>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700">{m.date || '—'}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700">{m.topics || '—'}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-600 max-w-[200px] truncate">{m.description || '—'}</td>
                      <td className="px-3 py-2 border border-slate-200 text-slate-700">{m.deadline || '—'}</td>
                      <td className="px-3 py-2 border border-slate-200 align-top"><CommentCell role="CIC" section="course_marketings" itemId={m.id} /></td>
                      <td className="px-3 py-2 border border-slate-200 align-top"><CommentCell role="AcademicHead" section="course_marketings" itemId={m.id} /></td>
                      <td className="px-3 py-2 border border-slate-200 align-top"><CommentCell role="PrincipalSSV" section="course_marketings" itemId={m.id} /></td>
                      <td className="px-3 py-2 border border-slate-200 align-top"><CommentCell role="CEO" section="course_marketings" itemId={m.id} /></td>
                      <td className="px-3 py-2 border border-slate-200"><StatusBadge status={m.status} /></td>
                      <td className="px-3 py-2 border border-slate-200">
                        <div className="flex items-center gap-2">
                          <CommentTrigger section="course_marketings" itemId={m.id} />
                          {m.status === 'Overdue' && (
                            <button data-testid={`close-mktg-${i}`} onClick={() => setClosingItem({ section: 'course_marketings', itemId: m.id })} className="text-emerald-600 hover:text-emerald-700 text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Close
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm py-4 text-center">No entries</p>
          )}
        </CardContent>
      </Card>

      {/* Approval Trail */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Approval Trail</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {report.cic_comments && (
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center text-xs font-bold text-cyan-700">C</div>
                <div>
                  <p className="text-xs font-bold text-slate-500">CIC Review</p>
                  <p className="text-sm text-slate-700 mt-0.5">{report.cic_comments}</p>
                  {report.cic_action_date && <p className="text-xs text-slate-400 mt-1">{new Date(report.cic_action_date).toLocaleString()}</p>}
                </div>
              </div>
            )}
            {report.academic_head_comments && (
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700">A</div>
                <div>
                  <p className="text-xs font-bold text-slate-500">Head Review</p>
                  <p className="text-sm text-slate-700 mt-0.5">{report.academic_head_comments}</p>
                  {report.academic_head_action_date && <p className="text-xs text-slate-400 mt-1">{new Date(report.academic_head_action_date).toLocaleString()}</p>}
                </div>
              </div>
            )}
            {report.principal_ssv_comments && (
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">P</div>
                <div>
                  <p className="text-xs font-bold text-slate-500">Principal Review</p>
                  <p className="text-sm text-slate-700 mt-0.5">{report.principal_ssv_comments}</p>
                  {report.principal_ssv_action_date && <p className="text-xs text-slate-400 mt-1">{new Date(report.principal_ssv_action_date).toLocaleString()}</p>}
                </div>
              </div>
            )}
            {!report.cic_comments && !report.academic_head_comments && !report.principal_ssv_comments && (
              <p className="text-sm text-slate-400 text-center py-2">No review comments yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Review Actions */}
      {(canCICReview || canAHReview || canPrincipalReview) && (
        <Card data-testid="review-actions-card" className="bg-white border-2 border-blue-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-blue-600">
              {canCICReview ? 'CIC Review' : canAHReview ? 'Head Review' : 'Principal Review'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Comments</label>
              <textarea
                data-testid="review-comments-input"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900 min-h-[80px] resize-y"
                placeholder="Add your review comments..."
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                data-testid="forward-btn"
                onClick={() => handleReview(canPrincipalReview ? 'review' : 'forward')}
                disabled={actionLoading}
                className="bg-blue-600 text-white hover:bg-blue-700 text-sm"
              >
                <Check className="w-3.5 h-3.5 mr-1.5" />
                {canPrincipalReview ? 'Mark as Reviewed' : 'Forward'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Close-Out Overdue Modal */}
      {closingItem && (
        <div data-testid="close-item-modal" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setClosingItem(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <h3 className="text-lg font-bold text-slate-900">Close Out Item</h3>
            </div>
            <p className="text-sm text-slate-500">Mark this overdue item as completed with a closing date.</p>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Closing Date</label>
              <Input data-testid="closing-date-input" type="date" value={closingDate} onChange={e => setClosingDate(e.target.value)} className="border-slate-300" />
            </div>
            <div className="flex items-center gap-3 justify-end">
              <Button variant="ghost" onClick={() => setClosingItem(null)} className="text-slate-600">Cancel</Button>
              <Button data-testid="confirm-close-btn" onClick={handleCloseItem} className="bg-emerald-600 text-white hover:bg-emerald-700">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Inline Comment (tag to activity) Modal */}
      {commentingItem && (
        <div data-testid="quick-comment-modal" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCommentingItem(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-slate-900">Add a comment</h3>
            </div>
            <p className="text-xs text-slate-500">
              Tagged to <span className="font-semibold text-slate-700">{SECTION_LABELS[commentingItem.section]}</span>. This message also appears in the Discussion thread.
            </p>
            <textarea
              data-testid="quick-comment-input"
              value={quickMsg}
              onChange={e => setQuickMsg(e.target.value)}
              autoFocus
              placeholder="Write a quick comment about this entry..."
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900 min-h-[100px] resize-y"
            />
            {['AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role) && (
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <input
                  data-testid="quick-visible-toggle"
                  type="checkbox"
                  checked={quickVisibleToFaculty}
                  onChange={e => setQuickVisibleToFaculty(e.target.checked)}
                  className="accent-blue-600 w-3.5 h-3.5"
                />
                <span>
                  {quickVisibleToFaculty
                    ? <span className="text-blue-700 font-semibold">Visible to faculty</span>
                    : <span className="text-slate-500 font-semibold">Internal note (hidden from faculty)</span>}
                </span>
              </label>
            )}
            <div className="flex items-center gap-3 justify-end">
              <Button variant="ghost" onClick={() => setCommentingItem(null)} className="text-slate-600">Cancel</Button>
              <Button
                data-testid="confirm-quick-comment-btn"
                onClick={handleSendQuickComment}
                disabled={sendingQuick || !quickMsg.trim()}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {sendingQuick ? 'Sending…' : 'Post Comment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Message Thread */}
      <Card data-testid="message-thread-card" className="bg-white border-2 border-slate-300 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-slate-700" />
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500">Discussion</CardTitle>
            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-semibold">{messages.length}</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-[400px] overflow-y-auto mb-4">
            {messages.length > 0 ? messages.map((m) => {
              const isMe = m.sender_id === user?._id;
              const msgId = `msg-${m._id}`;
              const isHi = highlightId === msgId;
              const tagged = m.activity_section && m.activity_item_id;
              return (
                <div key={m._id} id={msgId} className={`flex ${isMe ? 'justify-end' : 'justify-start'} ${isHi ? 'rounded-lg ring-2 ring-blue-400 ring-offset-2 ring-offset-white' : ''} transition-shadow`}>
                  <div className={`max-w-[80%] ${isMe ? 'order-2' : ''}`}>
                    {tagged && (
                      <button
                        type="button"
                        data-testid={`msg-activity-link-${m._id}`}
                        onClick={() => scrollToRow(m.activity_section, m.activity_item_id)}
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold mb-1 px-1.5 py-0.5 rounded ${isMe ? 'bg-blue-500/20 text-blue-100 hover:bg-blue-500/30' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                        title="Jump to the activity this comment refers to"
                      >
                        <Link2 className="w-3 h-3" />
                        View {SECTION_LABELS[m.activity_section] || 'activity'}
                      </button>
                    )}
                    <div className={`rounded-lg px-3.5 py-2.5 ${isMe ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-800'}`}>
                      <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                    </div>
                    <div className={`flex items-center gap-2 mt-1 ${isMe ? 'justify-end' : ''}`}>
                      <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ${ROLE_COLORS[m.sender_role] || 'bg-slate-100 text-slate-600'}`}>
                        {ROLE_LABELS[m.sender_role] || m.sender_role}
                      </span>
                      <span className="text-[10px] text-slate-400">{m.sender_name}</span>
                      <span className="text-[10px] text-slate-300">{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <p className="text-sm text-slate-400 text-center py-4">No messages yet. Start a discussion about this report.</p>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Send Message */}
          <div className="border-t border-slate-200 pt-3 space-y-2">
            {['AcademicHead', 'PrincipalSSV', 'SuperAdmin', 'CEO'].includes(user?.role) && (
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <input
                  data-testid="visible-to-faculty-toggle"
                  type="checkbox"
                  checked={visibleToFaculty}
                  onChange={e => setVisibleToFaculty(e.target.checked)}
                  className="accent-blue-600 w-3.5 h-3.5"
                />
                <span>
                  {visibleToFaculty
                    ? <span className="text-blue-700 font-semibold">Visible to faculty</span>
                    : <span className="text-slate-500 font-semibold">Internal note <span className="text-slate-400">(hidden from faculty — visible only to CIC and above)</span></span>}
                </span>
              </label>
            )}
            <div className="flex items-center gap-2">
              <input
                data-testid="message-input"
                type="text"
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                placeholder="Type a message..."
                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900 focus:outline-none"
              />
              <Button
                data-testid="send-message-btn"
                onClick={handleSendMessage}
                disabled={sendingMsg || !newMsg.trim()}
                className="bg-slate-900 text-white hover:bg-slate-800 text-sm px-4"
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
